import React, { useState, useEffect, ChangeEvent } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
} from "@mui/material";
import * as Yup from "yup";

export type DiscountType = "PERCENTAGE" | "FIXED_AMOUNT";
export type DiscountOn = "PRICE" | "CREDITS" | "BOTH";
export type CouponStatus = "ACTIVE" | "EXPIRED" | "INACTIVE";

export interface UpdateCouponPayload {
  code?: string;
  description?: string;
  discount_type?: DiscountType;
  discount_on?: DiscountOn;
  discount_value?: number | null;
  credit_bonus?: number | null;
  max_usage?: number | null;
  max_usage_per_user?: number | null;
  minimum_amount?: number | null;
  maximum_amount?: number | null;
  expiry?: string | null;
  status?: CouponStatus;
}

interface CouponUpdateProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: UpdateCouponPayload, params: string) => void;
  couponId: number;
  initialData: UpdateCouponPayload;
}

const validationSchema = Yup.object().shape({
  code: Yup.string()
    .trim()
    .uppercase()
    .min(1, "Code is too short")
    .max(10, "Code is too long")
    .required("Code is required"),
  description: Yup.string().required("Description is required"),
  discount_type: Yup.mixed<DiscountType>()
    .oneOf(["PERCENTAGE", "FIXED_AMOUNT"], "Invalid discount type")
    .required("Discount type is required"),
  discount_on: Yup.mixed<DiscountOn>()
    .oneOf(["PRICE", "CREDITS", "BOTH"], "Invalid discount on")
    .required("Discount on is required"),
  discount_value: Yup.number()
    .nullable()
    .when("discount_on", {
      is: (v: DiscountOn) => v === "PRICE" || v === "BOTH",
      then: (sch) =>
        sch.required("Discount value is required").min(0, "Must be ≥ 0"),
      otherwise: (sch) => sch.nullable(),
    })
    .when("discount_type", {
      is: "PERCENTAGE",
      then: (sch) => sch.max(100, "Percentage cannot exceed 100"),
    }),
  credit_bonus: Yup.number()
    .nullable()
    .when("discount_on", {
      is: (v: DiscountOn) => v === "CREDITS" || v === "BOTH",
      then: (sch) =>
        sch.required("Credit bonus is required").min(0, "Must be ≥ 0"),
      otherwise: (sch) => sch.nullable(),
    }),
  max_usage: Yup.number().nullable().min(1, "Must be at least 1"),
  max_usage_per_user: Yup.number().nullable().min(1, "Must be at least 1"),
  minimum_amount: Yup.number().nullable().min(0, "Must be ≥ 0"),
  maximum_amount: Yup.number()
    .nullable()
    .min(0, "Must be ≥ 0")
    .test("min-max", "Maximum must be ≥ minimum", function (val) {
      const min = this.parent.minimum_amount;
      return min == null || val == null || val >= min;
    }),
  expiry: Yup.date()
    .nullable()
    .min(new Date(), "Expiry must be in the future")
    .typeError("Invalid date"),
  status: Yup.mixed<CouponStatus>()
    .oneOf(["ACTIVE", "EXPIRED", "INACTIVE"], "Invalid status")
    .required("Status is required"),
});

const CouponUpdate: React.FC<CouponUpdateProps> = ({
  open,
  onClose,
  onSubmit,
  couponId,
  initialData,
}) => {
  const [values, setValues] = useState<UpdateCouponPayload>({
    code: "",
    description: "",
    discount_type: "PERCENTAGE",
    discount_on: "PRICE",
    discount_value: null,
    credit_bonus: null,
    max_usage: null,
    max_usage_per_user: null,
    minimum_amount: null,
    maximum_amount: null,
    expiry: null,
    status: "ACTIVE",
    ...initialData,
  });
  const [errors, setErrors] = useState<
    Partial<Record<keyof UpdateCouponPayload, string>>
  >({});

  useEffect(() => {
    let expiryLocal = "";
    if (initialData.expiry) {
      const dt = new Date(initialData.expiry);
      const tzOffsetMs = dt.getTimezoneOffset() * 60000;
      const localDt = new Date(dt.getTime() - tzOffsetMs);
      expiryLocal = localDt.toISOString().slice(0, 16);
    }
    setValues({
      code: initialData.code ?? "",
      description: initialData.description ?? "",
      discount_type: initialData.discount_type ?? "PERCENTAGE",
      discount_on: initialData.discount_on ?? "PRICE",
      discount_value: initialData.discount_value ?? null,
      credit_bonus: initialData.credit_bonus ?? null,
      max_usage: initialData.max_usage ?? null,
      max_usage_per_user: initialData.max_usage_per_user ?? null,
      minimum_amount: initialData.minimum_amount ?? null,
      maximum_amount: initialData.maximum_amount ?? null,
      expiry: expiryLocal,
      status: initialData.status ?? "ACTIVE",
    });
    setErrors({});
  }, [initialData, open]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> & { name?: string }
  ) => {
    const { name, value, type } = e.target;
    if (!name) return;

    let val: any = value;
    if (name === "code") {
      val = value.toUpperCase();
    }
    if (type === "number") {
      val = value === "" ? null : Number(value);
    }
    setValues((prev) => ({ ...prev, [name]: val }));
  };

  const handleSelect = (e: any) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      const valid = await validationSchema.validate(values, {
        abortEarly: false,
      });
      setErrors({});
      const payload: UpdateCouponPayload = {
        ...valid,
        expiry: valid.expiry ? new Date(valid.expiry).toISOString() : null,
      };
      onSubmit(payload, `${couponId}/`);
    } catch (err: any) {
      const formErrors: any = {};
      if (err.inner && Array.isArray(err.inner)) {
        err.inner.forEach((ve: any) => {
          if (ve.path) formErrors[ve.path] = ve.message;
        });
      } else if (err.path) {
        formErrors[err.path] = err.message;
      }
      setErrors(formErrors);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update Coupon</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            name="code"
            label="Code"
            value={values.code}
            onChange={handleChange}
            error={Boolean(errors.code)}
            helperText={errors.code}
            fullWidth
          />

          <TextField
            name="description"
            label="Description"
            value={values.description}
            onChange={handleChange}
            error={Boolean(errors.description)}
            helperText={errors.description}
            multiline
            rows={2}
            fullWidth
          />

          <FormControl fullWidth error={Boolean(errors.discount_type)}>
            <InputLabel>Discount Type</InputLabel>
            <Select
              name="discount_type"
              value={values.discount_type}
              label="Discount Type"
              onChange={handleSelect}
            >
              <MenuItem value="PERCENTAGE">Percentage</MenuItem>
              <MenuItem value="FIXED_AMOUNT">Fixed Amount</MenuItem>
            </Select>
            {errors.discount_type && (
              <Box sx={{ color: "error.main" }}>{errors.discount_type}</Box>
            )}
          </FormControl>

          <FormControl fullWidth error={Boolean(errors.discount_on)}>
            <InputLabel>Discount On</InputLabel>
            <Select
              name="discount_on"
              value={values.discount_on}
              label="Discount On"
              onChange={handleSelect}
            >
              <MenuItem value="PRICE">Price</MenuItem>
              <MenuItem value="CREDITS">Credits</MenuItem>
              <MenuItem value="BOTH">Both</MenuItem>
            </Select>
            {errors.discount_on && (
              <Box sx={{ color: "error.main" }}>{errors.discount_on}</Box>
            )}
          </FormControl>

          <TextField
            name="discount_value"
            label="Discount Value"
            type="number"
            inputProps={{ min: 0 }}
            value={values.discount_value ?? ""}
            onChange={handleChange}
            error={Boolean(errors.discount_value)}
            helperText={errors.discount_value}
            fullWidth
          />

          <TextField
            name="credit_bonus"
            label="Credit Bonus"
            type="number"
            inputProps={{ min: 0 }}
            value={values.credit_bonus ?? ""}
            onChange={handleChange}
            error={Boolean(errors.credit_bonus)}
            helperText={errors.credit_bonus}
            fullWidth
          />

          <TextField
            name="max_usage"
            label="Max Usage"
            type="number"
            inputProps={{ min: 1 }}
            value={values.max_usage ?? ""}
            onChange={handleChange}
            error={Boolean(errors.max_usage)}
            helperText={errors.max_usage}
            fullWidth
          />
          <TextField
            name="max_usage_per_user"
            label="Max Usage Per User"
            type="number"
            inputProps={{ min: 1 }}
            value={values.max_usage_per_user ?? ""}
            onChange={handleChange}
            error={Boolean(errors.max_usage_per_user)}
            helperText={errors.max_usage_per_user}
            fullWidth
          />

          <TextField
            name="minimum_amount"
            label="Minimum Amount"
            type="number"
            inputProps={{ min: 0 }}
            value={values.minimum_amount ?? ""}
            onChange={handleChange}
            error={Boolean(errors.minimum_amount)}
            helperText={errors.minimum_amount}
            fullWidth
          />
          <TextField
            name="maximum_amount"
            label="Maximum Amount"
            type="number"
            inputProps={{ min: 0 }}
            value={values.maximum_amount ?? ""}
            onChange={handleChange}
            error={Boolean(errors.maximum_amount)}
            helperText={errors.maximum_amount}
            fullWidth
          />

          <TextField
            name="expiry"
            label="Expiry"
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
            value={values.expiry ?? ""}
            onChange={handleChange}
            error={Boolean(errors.expiry)}
            helperText={errors.expiry}
            fullWidth
          />

          <FormControl fullWidth error={Boolean(errors.status)}>
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={values.status}
              label="Status"
              onChange={handleSelect}
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
              <MenuItem value="EXPIRED">Expired</MenuItem>
            </Select>
            {errors.status && (
              <Box sx={{ color: "error.main" }}>{errors.status}</Box>
            )}
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Update Coupon
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CouponUpdate;
